import { useState } from 'preact/hooks';
import { Chip, ChoiceChips, READ_TYPES } from '../ui/bits';
import { PillMenu } from '../ui/PillMenu';
import { useMatches } from '../shell/hooks';
import { loanState, quietedHold } from '../lib/libraryStates';
import { ESTIMATES } from '../mybooks/library';
import type { BookEdits } from './actions';
import { addDays, copyName, currentLoan, dayText, daysBetween, latestProgress, openRead, todayIn } from './model';
import { Sheet } from './reading';
import { FromField, hasFrom } from './gotFrom';
import type { BookData, Copy, CopyFormat, Loan } from './types';

// Book page Part 4, copies and library (Phase 6.1; agreed with Kristi Oct 27): Add a copy…, the
// library card's steps (Check in Libby ↗ + Update, Borrow it, Returned…, Renew…, Loan ended),
// Didn't finish… and Undo last step (was Not right?). Behind book_page.library.

type How = 'bought' | 'gift' | 'shared' | 'free' | 'library_loan';

const FORMATS: { key: string; format: CopyFormat; listen: boolean; label: string; store?: string }[] = [
  { key: 'kindle', format: 'kindle', listen: false, label: 'Kindle', store: 'Amazon' },
  { key: 'audible', format: 'audible', listen: true, label: 'Audible', store: 'Audible' },
  { key: 'paper', format: 'paper', listen: false, label: 'Paper' },
  { key: 'apple_books', format: 'apple_books', listen: false, label: 'Apple Books', store: 'Apple Books' },
  { key: 'librofm', format: 'librofm', listen: true, label: 'Libro.fm', store: 'Libro.fm' },
  { key: 'pdf_epub', format: 'pdf_epub', listen: false, label: 'PDF / EPUB' },
  { key: 'other', format: 'other', listen: false, label: 'Other' },
];
const LOAN_FORMATS: { key: string; format: CopyFormat; listen: boolean; label: string }[] = [
  { key: 'libby_kindle', format: 'kindle', listen: false, label: 'Kindle (Libby)' },
  { key: 'libby_audio', format: 'libby', listen: true, label: 'Libby audio' },
  { key: 'libby_ebook', format: 'libby', listen: false, label: 'Libby ebook' },
];
const LENGTHS = [7, 14, 21];

/** Add a copy… (Kristi, Oct 26–27): How · Format · Bought from (or From, for a gift or free copy: 0047) · Found through · Price · Acquired · Note · link. */
export function AddCopySheet({ book, edits, onClose }: { book: BookData; edits: BookEdits; onClose: () => void }) {
  const [how, setHow] = useState<How>('bought');
  const formats = how === 'library_loan' ? LOAN_FORMATS : FORMATS;
  const [fmt, setFmt] = useState(formats[0]!.key);
  const chosen = formats.find((f) => f.key === fmt) ?? formats[0]!;
  const [store, setStore] = useState<string | null>(null);
  const [found, setFound] = useState('');
  const [from, setFrom] = useState('');
  const [price, setPrice] = useState('');
  const [when, setWhen] = useState<'today' | 'other'>('today');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [size, setSize] = useState('');
  const [link, setLink] = useState('');
  const [loanStep, setLoanStep] = useState<'borrowed' | 'on_hold'>('borrowed');
  const [days, setDays] = useState(21);
  const [expected, setExpected] = useState('');
  const [start, setStart] = useState(false);
  const [readType, setReadType] = useState<string | null>(null);
  const [alsoCurrent, setAlsoCurrent] = useState(true);
  const [more, setMore] = useState({ publisher: '', imprint: '', edition: '', year: '', signed: false, number: '', of: '' });
  const [busy, setBusy] = useState(false);
  const reading = Boolean(openRead(book));
  const today = todayIn();
  const have = new Set(book.copies.filter((c) => c.state === 'have_it').map((c) => (c.how === 'library_loan' ? `lib:${c.format}` : c.format)));
  const storeShown: string = store ?? ('store' in chosen && typeof chosen.store === 'string' ? chosen.store : '');
  const names = book.readTypes.length ? book.readTypes.map((t) => t.name) : READ_TYPES.map((t) => t.name);
  const pickHow = (h: How) => {
    setHow(h);
    setFmt((h === 'library_loan' ? LOAN_FORMATS : FORMATS)[0]!.key);
    setStore(null);
    setFrom('');
  };
  const save = async () => {
    setBusy(true);
    const details: Record<string, unknown> = { how, format: chosen.format, listen: chosen.listen };
    if (how === 'bought' && storeShown.trim()) details.store = storeShown.trim();
    if (found.trim()) details.found_through = found.trim();
    if (hasFrom(how) && from.trim()) details.got_from = from.trim();
    if (price.trim()) details.price = price.trim();
    if (how !== 'library_loan' && when === 'other' && date) details.acquired_on = date;
    if (note.trim()) details.note = note.trim();
    // Pages or listening length (Kristi, Oct 27): so Progress can take a page or an Audible time.
    if (size.trim()) {
      if (chosen.listen) {
        const m = /^\s*(\d+)\s*[:h]\s*(\d{1,2})\s*m?\s*$/i.exec(size);
        if (m) details.listening_minutes = Number(m[1]) * 60 + Number(m[2]);
      } else if (/^\s*\d+\s*$/.test(size)) details.pages = Number(size);
    }
    if (link.trim()) details.link = link.trim();
    // More about this copy (paper only; 0039): the edition's facts (shared), signed and numbered (yours).
    if (how !== 'library_loan' && chosen.format === 'paper') {
      if (more.publisher.trim()) details.publisher = more.publisher.trim();
      if (more.imprint.trim()) details.imprint = more.imprint.trim();
      if (more.edition.trim()) details.edition_statement = more.edition.trim();
      if (/^\d{4}$/.test(more.year.trim())) details.published = { on: `${more.year.trim()}-01-01`, precision: 'year' };
      if (more.signed) details.signed = true;
      if (more.number.trim() || more.of.trim()) details.numbered = { number: more.number.trim(), of: more.of.trim() };
    }
    if (how === 'library_loan') details.loan = { step: loanStep, loan_days: days, expected_on: expected || null };
    if (!reading && start && (how !== 'library_loan' || loanStep === 'borrowed')) {
      details.start_reading = true;
      if (!book.readType && readType) details.read_type = readType;
    }
    if (reading && alsoCurrent && (how !== 'library_loan' || loanStep === 'borrowed')) details.also_current = true;
    const ok = await edits.act('copy_add', { details }, how === 'library_loan' ? 'Library loan added' : `${chosen.label} copy added`);
    setBusy(false);
    if (ok) onClose();
  };
  const readable = how !== 'library_loan' || loanStep === 'borrowed';
  return (
    <Sheet title={`Add a copy of ${book.work.title}`} sub="Undo puts every field back." onClose={onClose}>
      <ChoiceChips<How>
        class="bk-dialog-chips"
        label="How"
        choices={[
          ['bought', 'I bought it'],
          ['gift', 'A gift'],
          ['shared', 'Shared with me'],
          ['free', 'Free'],
          ['library_loan', 'Library loan'],
        ]}
        value={how}
        onPick={(k) => k && pickHow(k)}
      />
      <ChoiceChips
        class="bk-dialog-chips"
        label="Format"
        choices={formats.map((f) => {
          const owned = how === 'library_loan' ? have.has(`lib:${f.format}`) : have.has(f.format);
          return [
            f.key,
            <>
              {f.label}
              {owned ? <span class="chip-count"> · have it</span> : null}
            </>,
          ] as const;
        })}
        value={fmt}
        onPick={(k) => k && setFmt(k)}
      />
      {how === 'bought' ? (
        <div class="bk-when">
          <label class="bk-field-inline">
            Bought from
            <input type="text" maxLength={100} value={storeShown} onInput={(e) => setStore((e.target as HTMLInputElement).value)} />
          </label>
          <label class="bk-field-inline">
            Price
            <input type="text" inputMode="decimal" placeholder="$" maxLength={10} value={price} onInput={(e) => setPrice((e.target as HTMLInputElement).value)} />
          </label>
        </div>
      ) : null}
      {hasFrom(how) ? <FromField how={how} value={from} onChange={setFrom} /> : null}
      {how !== 'library_loan' ? (
        <>
          <label class="bk-field">
            <span class="bk-label">Found through (optional · only you see this)</span>
            <input type="text" maxLength={100} placeholder="BookBub, Kindle Daily Deal…" value={found} onInput={(e) => setFound((e.target as HTMLInputElement).value)} />
          </label>
          <div class="bk-when">
            <span class="bk-label">Acquired</span>
            <Chip small label="Today" on={when === 'today'} onToggle={() => setWhen('today')} />
            <Chip small label="Another date" on={when === 'other'} onToggle={() => setWhen('other')} />
            {when === 'other' ? <input type="date" aria-label="Acquired on" value={date} onInput={(e) => setDate((e.target as HTMLInputElement).value)} /> : null}
          </div>
        </>
      ) : (
        <>
          <ChoiceChips
            class="bk-dialog-chips"
            label="Loan"
            choices={
              [
                ['borrowed', 'Borrowed now'],
                ['on_hold', 'Placed a hold'],
              ] as const
            }
            value={loanStep}
            onPick={(k) => k && setLoanStep(k)}
          />
          {loanStep === 'borrowed' ? (
            <div class="bk-when" role="group" aria-label="Loan length">
              {LENGTHS.map((n) => (
                <Chip key={n} small label={`${n} days`} on={days === n} onToggle={() => setDays(n)} />
              ))}
              <span class="bk-quiet">due {dayText(addDays(today, days), today)}</span>
            </div>
          ) : (
            <label class="bk-field-inline">
              Libby’s estimate
              <input type="date" aria-label="Expected on" value={expected} onInput={(e) => setExpected((e.target as HTMLInputElement).value)} />
            </label>
          )}
        </>
      )}
      <label class="bk-field">
        <span class="bk-label">{how === 'library_loan' ? 'Libby “Read now with Kindle” link (optional)' : 'Paste now: a Kindle or Audible link (optional)'}</span>
        <input type="url" maxLength={2048} placeholder="https://…" value={link} onInput={(e) => setLink((e.target as HTMLInputElement).value)} />
      </label>
      <label class="bk-field">
        <span class="bk-label">{chosen.listen ? 'Listening length (optional, h:mm)' : 'Pages (optional)'}</span>
        <input
          type="text"
          inputMode={chosen.listen ? 'text' : 'numeric'}
          maxLength={10}
          placeholder={chosen.listen ? 'e.g. 12:44' : 'e.g. 352'}
          value={size}
          onInput={(e) => setSize((e.target as HTMLInputElement).value)}
        />
      </label>
      {how !== 'library_loan' && chosen.format === 'paper' ? (
        <details class="bk-copy-more">
          <summary>More about this copy</summary>
          <p class="bk-quiet">Publisher, imprint or series, edition and year are shared facts: as the admin, for everyone. They make a new edition for this copy.</p>
          <div class="bk-when">
            <label class="bk-field-inline">
              Publisher
              <input type="text" maxLength={300} placeholder="Easton Press" value={more.publisher} onInput={(e) => setMore({ ...more, publisher: (e.target as HTMLInputElement).value })} />
            </label>
            <label class="bk-field-inline">
              Year
              <input type="text" inputMode="numeric" maxLength={4} size={5} placeholder="1977" value={more.year} onInput={(e) => setMore({ ...more, year: (e.target as HTMLInputElement).value })} />
            </label>
          </div>
          <label class="bk-field">
            <span class="bk-label">Imprint or series</span>
            <input type="text" maxLength={300} placeholder="The 100 Greatest Books Ever Written" value={more.imprint} onInput={(e) => setMore({ ...more, imprint: (e.target as HTMLInputElement).value })} />
          </label>
          <label class="bk-field">
            <span class="bk-label">Edition</span>
            <input type="text" maxLength={300} placeholder="Collector’s edition" value={more.edition} onInput={(e) => setMore({ ...more, edition: (e.target as HTMLInputElement).value })} />
          </label>
          <div class="bk-when">
            <label class="bk-check">
              <input type="checkbox" checked={more.signed} onChange={(e) => setMore({ ...more, signed: (e.target as HTMLInputElement).checked })} />
              Signed
            </label>
            <span class="bk-label">Numbered</span>
            <input type="text" inputMode="numeric" aria-label="Number" maxLength={9} size={6} placeholder="412" value={more.number} onInput={(e) => setMore({ ...more, number: (e.target as HTMLInputElement).value })} />
            <span>of</span>
            <input type="text" inputMode="numeric" aria-label="Of how many" maxLength={9} size={7} placeholder="1,000" value={more.of} onInput={(e) => setMore({ ...more, of: (e.target as HTMLInputElement).value })} />
          </div>
        </details>
      ) : null}
      <label class="bk-field">
        <span class="bk-label">Note (optional · only you see this)</span>
        <input type="text" maxLength={5000} value={note} onInput={(e) => setNote((e.target as HTMLInputElement).value)} />
      </label>
      {readable && !reading ? (
        <label class="bk-check">
          <input type="checkbox" checked={start} onChange={(e) => setStart((e.target as HTMLInputElement).checked)} />
          Start reading now
        </label>
      ) : null}
      {readable && !reading && start && !book.readType ? (
        <div class="bk-dialog-chips" role="group" aria-label="Read type">
          {names.map((n) => (
            <Chip key={n} small label={n} on={readType === n} onToggle={() => setReadType(readType === n ? null : n)} />
          ))}
        </div>
      ) : null}
      {readable && reading ? (
        <label class="bk-check">
          <input type="checkbox" checked={alsoCurrent} onChange={(e) => setAlsoCurrent((e.target as HTMLInputElement).checked)} />
          Also use it on the current read
        </label>
      ) : null}
      <div class="bk-buttons">
        <button type="button" class="btn-main" disabled={busy} onClick={() => void save()}>
          Save copy
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}

/** The Libby page for a loan's library (opens the book's search there). */
const libbyLink = (book: BookData, loan: Loan) => {
  const code = loan.libby_code ?? book.libbyCode;
  const q = encodeURIComponent(`${book.work.title} ${book.authors[0] ?? ''}`.trim());
  return code ? `https://libbyapp.com/search/${code}/search/query-${q}/page-1` : 'https://libbyapp.com/shelf';
};

/**
 * The library card's steps (decisions doc › Library ladder): On hold · Ready · Borrowed ·
 * Reading · Loan ended, each with what you can do, plus Undo last step (Kristi, Oct 31: was "Not right?", which read like a question).
 */
export function LoanSteps({
  book,
  copy,
  edits,
  onFinished,
  onDidntFinish,
  onStart,
  stopNearby = false,
}: {
  book: BookData;
  copy: Copy;
  edits: BookEdits;
  onFinished: () => void;
  onDidntFinish: () => void;
  onStart: () => void;
  /** The place already shows its own Stop reading… (a Currently Reading card), so the renew
   *  window doesn't add a second one. */
  stopNearby?: boolean;
}) {
  const loan = currentLoan(copy);
  const [asking, setAsking] = useState<'update' | 'renew' | 'returned' | 'borrow' | 'hold' | null>(null);
  const [expected, setExpected] = useState('');
  const [est, setEst] = useState('30');
  const [estDate, setEstDate] = useState('');
  const wide = useMatches('(min-width: 700px)');
  const today = todayIn();
  if (!loan) return null;
  const like = { step: loan.step, dueOn: loan.due_on, expectedOn: loan.expected_on, checkDays: loan.check_days, quietUntil: loan.quiet_until ?? null };
  // Checked, not ready yet (filters review, Kristi Nov 2): a hold in its Check Libby window can be quieted.
  const checking = loanState(like, false, today) === 'check_libby';
  const quieted = quietedHold(like, today);
  const quiet = (until: string | null, message: string) => {
    setAsking(null);
    return edits.act('loan_quiet', { loan: loan.id, until }, message);
  };
  const QUIET = [
    { value: addDays(today, 2), label: 'Quiet for 2 days' },
    { value: addDays(today, 7), label: 'Quiet for a week' },
    ...(loan.expected_on && loan.expected_on > today ? [{ value: loan.expected_on, label: `Quiet until it’s expected (${dayText(loan.expected_on, today)})` }] : []),
  ];
  const read = openRead(book);
  const onRead = Boolean(read?.copies.some((c) => c.copy_id === copy.id && !c.stopped_using_on));
  const step = (s: string, details: Record<string, unknown>, message: string) => {
    setAsking(null);
    return edits.act('loan_step', { copy: copy.id, step: s, details }, message);
  };
  const ended = loan.step === 'borrowed' && loan.due_on !== null && daysBetween(today, loan.due_on) < 0;
  const renewFrom = loan.due_on ? addDays(loan.due_on, -3) : null;
  const notRight = (
    <button type="button" class="btn-link" onClick={() => void edits.act('loan_not_right', { copy: copy.id }, 'Undone: the latest library step')}>
      Undo last step
    </button>
  );
  const lengths = (s: 'borrowed', label: string) => (
    <span class="bk-when" role="group" aria-label="Loan length">
      {LENGTHS.map((n) => (
        <Chip key={n} small plain label={`${n} days`} on={false} onToggle={() => void step(s, { loan_days: n }, `${label} for ${n} days`)} />
      ))}
    </span>
  );
  return (
    <div class="bk-loan-steps">
      {loan.step === 'on_hold' ? (
        <div class="bk-buttons">
          <a class="btn-book" href={libbyLink(book, loan)} target="_blank" rel="noopener noreferrer" onClick={() => setAsking('update')}>
            Check in Libby ↗
          </a>
          <button type="button" class="btn-secondary" aria-expanded={asking === 'update'} onClick={() => setAsking(asking === 'update' ? null : 'update')}>
            Update
          </button>
          {checking ? (
            <PillMenu
              label="Not ready yet"
              ariaLabel="Checked, not ready yet"
              sheetTitle="Checked, not ready yet"
              choices={QUIET}
              current={QUIET[0]!.value}
              onPick={(until) => void quiet(until, `Checked, not ready yet: quiet until ${dayText(until, today)}`)}
            />
          ) : null}
          {quieted ? (
            <button type="button" class="btn-secondary" onClick={() => void quiet(null, 'Check it again now')}>
              Check now
            </button>
          ) : null}
          {notRight}
        </div>
      ) : null}
      {asking === 'update' ? (
        <div class="heads-up bk-ask" role="group" aria-label="What did Libby say?">
          Back from Libby? What did it say?
          <button type="button" class="btn-secondary" onClick={() => void step('ready', {}, 'Ready to borrow')}>
            It’s ready
          </button>
          <button type="button" class="btn-secondary" onClick={() => setAsking('borrow')}>
            I borrowed it
          </button>
          <button type="button" class="btn-secondary" onClick={() => void step('not_yet', expected ? { expected_on: expected } : {}, 'Still on hold')}>
            Not yet
          </button>
          <input type="date" aria-label="Libby’s new date (optional)" value={expected} onInput={(e) => setExpected((e.target as HTMLInputElement).value)} />
        </div>
      ) : null}
      {asking === 'borrow' ? lengths('borrowed', 'Borrowed') : null}
      {loan.step === 'ready' ? (
        <div class="bk-buttons">
          <button type="button" class="btn-main" onClick={() => setAsking(asking === 'borrow' ? null : 'borrow')}>
            Borrow it
          </button>
          <button type="button" class="btn-secondary" onClick={() => void step('deliver_later', {}, 'Deliver later')}>
            Deliver later
          </button>
          <button type="button" class="btn-link" onClick={() => void step('let_go', {}, 'Let the hold go')}>
            Let it go
          </button>
          {notRight}
        </div>
      ) : null}
      {loan.step === 'borrowed' && !onRead && ended ? (
        // A loan that ended on a copy you're not reading (Kristi, Oct 29): Libby has returned it
        // already, so "Returned", not "Return unread".
        <div class="bk-buttons">
          <button type="button" class="btn-book" onClick={() => void step('returned', {}, `Returned ${copyName(book, copy)}`)}>
            Returned
          </button>
          <a class="btn-book" href="https://libbyapp.com/shelf/loans" target="_blank" rel="noopener noreferrer">
            Get it again in Libby ↗
          </a>
          {notRight}
        </div>
      ) : null}
      {loan.step === 'borrowed' && !onRead && !ended ? (
        <div class="bk-buttons">
          {!read ? (
            <button type="button" class="btn-main" onClick={onStart}>
              Start reading
            </button>
          ) : null}
          <button type="button" class="btn-book" onClick={() => void step('returned', { unread: true }, 'Returned unread')}>
            Return unread
          </button>
          {notRight}
        </div>
      ) : null}
      {loan.step === 'borrowed' && onRead ? (
        <>
          {ended ? <div class="heads-up">Loan ended · at {Math.round(latestProgress(read) ?? 0)}% · did you finish?</div> : null}
          <div class="bk-buttons">
            <button type="button" class="btn-book" aria-expanded={asking === 'returned'} onClick={() => setAsking(asking === 'returned' ? null : 'returned')}>
              {ended ? 'Did you finish?' : 'Returned…'}
            </button>
            {(renewFrom && today >= renewFrom) || ended ? (
              <a class="btn-book" href="https://libbyapp.com/shelf/loans" target="_blank" rel="noopener noreferrer" onClick={() => setAsking('renew')}>
                {ended ? 'Get it again in Libby ↗' : 'Renew in Libby ↗'}
              </a>
            ) : null}
            {/* Not every loan can be renewed (Kristi, Nov 2 and 3): renew it, give the details of a new
                hold, or stop reading (the shared Stop reading sheet, which returns the loan). On a phone
                the last two sit behind ▾ beside Renew (one action plus ▾, as Owned copies' rows). A
                Currently Reading card already has its own Stop reading…, so there it isn't repeated. */}
            {renewFrom && today >= renewFrom && !ended ? (
              wide || stopNearby ? (
                <>
                  <a class="btn-book" href={libbyLink(book, loan)} target="_blank" rel="noopener noreferrer" onClick={() => setAsking('hold')}>
                    Place a new hold in Libby ↗
                  </a>
                  {!stopNearby ? (
                    <button type="button" class="btn-book" onClick={onDidntFinish}>
                      Stop reading…
                    </button>
                  ) : null}
                </>
              ) : (
                <PillMenu
                  label="More"
                  ariaLabel="More for this loan"
                  sheetTitle={book.work.title}
                  choices={[
                    { value: 'hold', label: 'Place a new hold in Libby ↗' },
                    { value: 'stop', label: 'Stop reading…' },
                  ]}
                  onPick={(v) => {
                    if (v === 'hold') {
                      window.open(libbyLink(book, loan), '_blank', 'noopener');
                      setAsking('hold');
                    } else onDidntFinish();
                  }}
                />
              )
            ) : null}
            {notRight}
          </div>
          {asking === 'returned' ? (
            <div class="heads-up bk-ask" role="group" aria-label="Did you finish it?">
              Did you finish it?
              <button
                type="button"
                class="btn-main"
                onClick={async () => {
                  if (await step('returned', {}, `Returned ${copyName(book, copy)}`)) onFinished();
                }}
              >
                Finished it
              </button>
              <button type="button" class="btn-secondary" onClick={() => { setAsking(null); onDidntFinish(); }}>
                Didn’t finish…
              </button>
            </div>
          ) : null}
          {asking === 'renew' ? (
            <div class="heads-up bk-ask" role="group" aria-label="What did Libby let you do?">
              What did Libby let you do?
              <button type="button" class="btn-secondary" onClick={() => void step('renewed', {}, 'Renewed')}>
                Renewed it
              </button>
              <button type="button" class="btn-secondary" onClick={() => void step('renewed', {}, 'Borrowed it again')}>
                Borrowed it again
              </button>
            </div>
          ) : null}
          {asking === 'hold' ? (
            // Holds to place's own control (Kristi, Nov 2): Libby's estimate, then Save the hold.
            <div class="heads-up bk-ask" role="group" aria-label="The new hold">
              Placed it? What’s Libby’s estimate?
              <ChoiceChips class="mb-ask-chips" label="Libby’s estimate" small choices={ESTIMATES.map((x) => [x.key, x.label] as const)} value={est} onPick={(k) => k && setEst(k)}>
                {est === 'date' ? <input type="date" aria-label="Libby’s date" value={estDate} onInput={(e) => setEstDate((e.target as HTMLInputElement).value)} /> : null}
              </ChoiceChips>
              <button
                type="button"
                class="btn-main"
                disabled={est === 'date' && !estDate}
                onClick={() => {
                  const e = ESTIMATES.find((x) => x.key === est)!;
                  const expectedOn = e.days !== null ? addDays(today, e.days) : est === 'date' ? estDate : '';
                  void step('returned', { new_hold: true, didnt_finish: true, percent: latestProgress(read), expected_on: expectedOn }, 'Back in line: a new hold');
                }}
              >
                Save the hold
              </button>
              <button type="button" class="btn-secondary" onClick={() => setAsking(null)}>
                Didn’t place it
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      {loan.step === 'returned' || loan.step === 'cancelled' ? (
        <div class="bk-buttons">
          <a class="btn-book" href={libbyLink(book, loan)} target="_blank" rel="noopener noreferrer">
            Get it again in Libby ↗
          </a>
          <button type="button" class="btn-secondary" onClick={() => void step('new_hold', {}, 'New hold placed')}>
            I placed a new hold
          </button>
          {notRight}
        </div>
      ) : null}
    </div>
  );
}

